import {Component} from 'react';
import PropTypes from 'prop-types';
import {calculateDuration} from '@deliveriesondemand/dod-utils';

import {Control, ControlGroup} from '-/components/fields/Control';
import {NumberInput} from '-/components/fields/NumberInput';
import Address from '-/components/general/Address';
import CreditorService from '-/services/CreditorService';
import Legend from '-/components/fields/Legend';
import Button from '-/components/general/Button';
import {isFinite} from '-/util/lodash';

class PriorAddress extends Component {
    static propTypes = {
        value: PropTypes.arrayOf(PropTypes.shape({
            priorAddress: PropTypes.shape({
                street1: PropTypes.string,
                street2: PropTypes.string,
                city: PropTypes.string,
                state: PropTypes.string,
                county: PropTypes.string,
                zip: PropTypes.string,
                formatted: PropTypes.string
            }),
            monthsAtPriorAddress: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string
            ]),
            yearsAtPriorAddress: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string
            ])
        })),
        limitYears: PropTypes.number,
        onChange: PropTypes.func
    };

    static defaultProps = {
        value: [],
        limitYears: 0,
        onChange: () => {}
    };

    constructor(props) {
        super(props);

        this.onStateSet = this.onStateSet.bind(this);
        this.handleItemAdd = this.handleItemAdd.bind(this);

        this.state = {
            value: []
        };
    }

    getErrors() {
        const {monthsNeeded} = this.props;
        const monthsSoFar = this.getMonthsSoFar();

        if (monthsSoFar < monthsNeeded) {
            const extraMonthsNeeded = monthsNeeded - monthsSoFar;
            const {months, years} = calculateDuration(0, extraMonthsNeeded);
            const yearLabel = `${years} more year${years > 1 ? 's' : ''}`;
            const monthLabel = `${months} more month${months > 1 ? 's' : ''}`;

            return [`Please provide ${years ? yearLabel : ''}${years && months ? ' and ' : ''}${months ? monthLabel : ''} of address history`];
        } else {
            const {value} = this.state;

            for (let i = 0; i < value.length; i++) {
                const prior = value[i];
                const {priorAddress = {}, yearsAtPriorAddress: yearsRaw, monthsAtPriorAddress: monthsRaw} = prior;
                const {street1} = priorAddress;
                const yearsAtPriorAddress = isNaN(yearsRaw) ? 0 : yearsRaw;
                const monthsAtPriorAddress = isNaN(monthsRaw) ? 0 : monthsRaw;


                if (!street1 || (!isFinite(yearsAtPriorAddress) && !isFinite(monthsAtPriorAddress))) {
                    return ['Please fill in every prior address field'];
                }
            }
        }
        return [];
    }

    componentDidMount() {
        const {value} = this.props;
        this.setState({value}, this.onStateSet);
    }

    componentWillReceiveProps(nextProps) {
        this.setState({value: nextProps.value}, this.onStateSet);
    }

    onChange() {
        const {onChange} = this.props;
        const {value} = this.state;
        onChange(value);
    }

    handleFieldChange(i, key, name) {
        const {value} = this.state;
        value[i][key] = name;
        this.setState({value}, this.onChange);
    }

    onStateSet() {
        const {value} = this.state;

        // ensure we always have at least one item
        if (value.length === 0) {
            this.setState({value: [{
                priorAddress: {},
                monthsAtPriorAddress: '',
                yearsAtPriorAddress: ''
            }]});
        }
    }

    handleItemDelete(i) {
        let {value} = this.state;

        if (i === 0) {
            if (value.length === 1) {
                value.splice(0, 1, {
                    priorAddress: {},
                    monthsAtPriorAddress: '',
                    yearsAtPriorAddress: ''
                });
                this.setState({value}, this.onChange);
                return;
            }
        }

        value.splice(i, 1);
        this.setState({value}, this.onChange);
    }

    handleItemAdd() {
        const {value} = this.state;
        value.push({
            priorAddress: {},
            monthsAtPriorAddress: '',
            yearsAtPriorAddress: ''
        });
        this.setState({value}, () => {
            this.onChange();
            window.scrollTo(0, document.body.scrollHeight);
        });
    }

    getMonthsSoFar() {
        const {value} = this.state;

        return value.map(item => {
            const {monthsAtPriorAddress: months, yearsAtPriorAddress: years} = item;
            const {months: calculatedMonths, years: calculatedYears} = calculateDuration(years, months);
            const finalMonths = isNaN(calculatedMonths) ? 0 : calculatedMonths;
            const finalYears = isNaN(calculatedYears) ? 0 : calculatedYears;

            return finalMonths + (finalYears * 12);
        }).reduce((total, item) => total + item, 0);
    }

    render() {
        const {value} = this.state;
        const {monthsNeeded} = this.props;
        const monthsSoFar = this.getMonthsSoFar();

        return (
            <div>
                <ControlGroup>
                    <Legend>Prior Address</Legend>
                    <p className="info">Because you have lived less than {CreditorService.getYearsResidencyHistoryRequired()} years at your current place, we need to collect a prior address.</p>
                </ControlGroup>
                {value.map((item, i) => {
                    const showDeleteBtn = value.length === 1 ? false : i > 0;
                    const showAddBtn = (monthsSoFar < monthsNeeded && i === value.length - 1);
                    const yearsAtPriorAddress = item.yearsAtPriorAddress || 0;
                    const monthsAtPriorAddress = item.monthsAtPriorAddress || 0;
                    return (
                        <ControlGroup labelWidth={200} key={i} className="test">
                            <Address
                                value={item.priorAddress}
                                onChange={value => this.handleFieldChange(i, 'priorAddress', value)} />
                            <Control>
                                <NumberInput
                                    value={yearsAtPriorAddress}
                                    label="Years At Address"
                                    placeholder="X Years"
                                    onChange={value => this.handleFieldChange(i, 'yearsAtPriorAddress', parseInt(value))} />
                            </Control>
                            <Control>
                                <NumberInput
                                    value={monthsAtPriorAddress}
                                    label="Months At Address"
                                    placeholder="X Months"
                                    onChange={value => this.handleFieldChange(i, 'monthsAtPriorAddress', parseInt(value))} />
                            </Control>
                            {showAddBtn ? (
                                <Button
                                    btnStyle="add"
                                    onClick={this.handleItemAdd}>
                                    <i className="fa fa-plus" /> Add
                                </Button>
                            ) : null}
                            {showDeleteBtn ? (
                                <Button
                                    btnStyle="delete"
                                    onClick={() => this.handleItemDelete(i)}>
                                    <i className="fa fa-close" /> Delete
                                </Button>
                            ) : null}
                        </ControlGroup>
                    );
                })}
            </div>
        );
    }
}

export default PriorAddress;
